import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { X, Save, Shield, CheckCircle, Users, AlertTriangle, Search, CheckCheck, RotateCcw } from 'lucide-react';
import { useUserPermissions } from 'features/administrativo/hooks/useUsers';
import { useAllPermissions } from 'hooks/useAllPermissions';
import api from 'services/api';

const normalizePermissions = (permissionNames) => {
  if (!Array.isArray(permissionNames)) {
    return [];
  }

  return Array.from(new Set(permissionNames)).sort();
};

const CATEGORY_LABELS = {
  dashboard: 'Dashboard',
  financeiro: 'Financeiro',
  contratos: 'Contratos',
  comercial: 'Comercial',
  operacional: 'Operacional',
  administrativo: 'Administrativo',
  config: 'Configurações',
  solicitacoes: 'Solicitações de Acesso',
  estoque: 'Estoque',
  automatizacoes: 'Automações',
  Outros: 'Outros'
};

const UserPermissions = ({ user, onClose, onUpdate }) => {
  const { permissions, loading, syncPermissions } = useUserPermissions(user?.id);
  const { permissions: allPermissions, loading: allPermissionsLoading } = useAllPermissions();

  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [initialPermissionsSnapshot, setInitialPermissionsSnapshot] = useState('[]');
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const previousFocusedElementRef = useRef(null);
  const presetSelectRef = useRef(null);
  const closeButtonRef = useRef(null);

  const dialogTitleId = 'user-permissions-title';
  const dialogDescriptionId = 'user-permissions-description';
  const saveErrorId = 'user-permissions-save-error';
  const presetSelectId = 'user-permissions-preset-select';

  const groupedPermissions = useMemo(() => {
    const grouped = {};

    if (Array.isArray(allPermissions)) {
      allPermissions.forEach((permission) => {
        const category = permission.category || 'Outros';
        if (!grouped[category]) {
          grouped[category] = [];
        }
        grouped[category].push(permission);
      });
    }

    return grouped;
  }, [allPermissions]);

  const totalPermissions = useMemo(() => {
    return Object.values(groupedPermissions).flat().length;
  }, [groupedPermissions]);
  const selectedCount = selectedPermissions.length;
  const remainingPermissions = Math.max(totalPermissions - selectedCount, 0);
  const completionRate = totalPermissions > 0 ? Math.round((selectedCount / totalPermissions) * 100) : 0;

  const filteredGroupedPermissions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return groupedPermissions;

    const filtered = {};

    Object.entries(groupedPermissions).forEach(([category, categoryPermissions]) => {
      const categoryLabel = (CATEGORY_LABELS[category] || category).toLowerCase();
      const categoryMatches = categoryLabel.includes(term) || category.toLowerCase().includes(term);

      if (categoryMatches) {
        filtered[category] = categoryPermissions;
        return;
      }

      const permissionsMatches = categoryPermissions.filter((permission) => {
        const displayName = String(permission.display_name || '').toLowerCase();
        const description = String(permission.description || '').toLowerCase();
        const permissionName = String(permission.name || '').toLowerCase();
        return displayName.includes(term) || description.includes(term) || permissionName.includes(term);
      });

      if (permissionsMatches.length > 0) {
        filtered[category] = permissionsMatches;
      }
    });

    return filtered;
  }, [groupedPermissions, searchTerm]);

  const hasUnsavedChanges = useMemo(() => {
    const currentSnapshot = JSON.stringify(normalizePermissions(selectedPermissions));
    return currentSnapshot !== initialPermissionsSnapshot;
  }, [selectedPermissions, initialPermissionsSnapshot]);

  useEffect(() => {
    previousFocusedElementRef.current = document.activeElement;

    requestAnimationFrame(() => {
      if (presetSelectRef.current) {
        presetSelectRef.current.focus();
      } else {
        closeButtonRef.current?.focus();
      }
    });
  }, []);

  useEffect(() => {
    const loadPresets = async () => {
      try {
        const response = await api.get('/permission-presets');

        if (response.data && response.data.success) {
          setPresets(response.data.data);
        } else {
          setPresets(response.data || []);
        }
      } catch (error) {
        console.error('Erro ao carregar presets:', error);
        setPresets([]);
      }
    };

    loadPresets();
  }, []);

  useEffect(() => {
    if (Array.isArray(permissions)) {
      const userPermissionNames = permissions.map((permission) => permission.name);
      const normalized = normalizePermissions(userPermissionNames);
      setSelectedPermissions(normalized);
      setInitialPermissionsSnapshot(JSON.stringify(normalized));
      setSaveError('');
    }
  }, [permissions]);

  const restoreFocusAndClose = useCallback(() => {
    onClose?.();
    requestAnimationFrame(() => {
      const triggerElement = previousFocusedElementRef.current;
      if (triggerElement && typeof triggerElement.focus === 'function') {
        triggerElement.focus();
      }
    });
  }, [onClose]);

  const requestClose = useCallback(() => {
    if (isSubmitting) return;

    if (hasUnsavedChanges) {
      const shouldClose = window.confirm('Existem alterações de permissões não salvas. Deseja fechar mesmo assim?');
      if (!shouldClose) return;
    }

    restoreFocusAndClose();
  }, [hasUnsavedChanges, isSubmitting, restoreFocusAndClose]);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      requestClose();
    };

    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [requestClose]);

  const handleBackdropMouseDown = (event) => {
    if (event.target === event.currentTarget) {
      requestClose();
    }
  };

  const handlePermissionToggle = (permissionName) => {
    if (isSubmitting) return;

    setSaveError('');
    setSelectedPermissions((prev) => {
      if (prev.includes(permissionName)) {
        return prev.filter((permission) => permission !== permissionName);
      }
      return [...prev, permissionName];
    });
  };

  const handlePresetChange = (presetName) => {
    setSaveError('');
    setSelectedPreset(presetName);
  };

  const handleApplyPreset = () => {
    if (!selectedPreset || isSubmitting) return;

    const preset = presets.find((item) => item.name === selectedPreset);
    if (preset && Array.isArray(preset.permissions)) {
      setSaveError('');
      setSelectedPermissions(normalizePermissions(preset.permissions));
    }
  };

  const handleSave = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setSaveError('');

    try {
      await syncPermissions(selectedPermissions);

      if (onUpdate) {
        onUpdate();
      }

      restoreFocusAndClose();
    } catch (error) {
      console.error('Erro ao salvar permissões:', error);
      const message =
        error?.response?.data?.message ||
        'Não foi possível salvar as permissões. Verifique sua conexão e tente novamente.';
      setSaveError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectAllPermissions = () => {
    if (isSubmitting) return;

    const allPermissionNames = Object.values(groupedPermissions)
      .flat()
      .map((permission) => permission.name);

    setSaveError('');
    setSelectedPermissions(normalizePermissions(allPermissionNames));
  };

  const handleClearAllPermissions = () => {
    if (isSubmitting) return;
    setSaveError('');
    setSelectedPermissions([]);
  };

  const handleSelectCategoryPermissions = (categoryPermissions) => {
    if (isSubmitting) return;

    const categoryPermissionNames = categoryPermissions.map((permission) => permission.name);
    const merged = new Set([...selectedPermissions, ...categoryPermissionNames]);

    setSaveError('');
    setSelectedPermissions(Array.from(merged));
  };

  const handleClearCategoryPermissions = (categoryPermissions) => {
    if (isSubmitting) return;

    const categoryPermissionNames = new Set(categoryPermissions.map((permission) => permission.name));
    setSaveError('');
    setSelectedPermissions((prev) => prev.filter((permissionName) => !categoryPermissionNames.has(permissionName)));
  };

  if (loading || allPermissionsLoading) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
        <div
          className="rounded-lg shadow-xl p-8 max-w-md w-full theme-surface border"
          style={{ borderColor: 'var(--border)' }}
          role="status"
          aria-live="polite"
        >
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
            </div>
            <h3 className="text-lg font-medium theme-text mb-2">Carregando permissões...</h3>
            <p className="text-sm theme-text-muted">Aguarde enquanto carregamos os dados...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 overflow-y-auto"
      onMouseDown={handleBackdropMouseDown}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={dialogTitleId}
        aria-describedby={dialogDescriptionId}
        className="rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col theme-surface border"
        style={{ borderColor: 'var(--border)' }}
      >
        <div className="flex items-center justify-between p-4 sm:p-6 border-b" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center space-x-3 min-w-0 flex-1">
            <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0">
              <Shield className="h-6 w-6 text-purple-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 id={dialogTitleId} className="text-lg font-semibold theme-text">
                Gerenciar Permissões
              </h3>
              <p id={dialogDescriptionId} className="text-sm theme-text-muted truncate">
                {user?.full_name} ({user?.corporate_email})
              </p>
            </div>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={requestClose}
            disabled={isSubmitting}
            aria-label="Fechar modal de gerenciamento de permissões"
            className="h-11 w-11 inline-flex items-center justify-center rounded-lg transition-colors hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 flex-shrink-0"
            style={{ background: 'var(--surface-muted)' }}
          >
            <X className="h-5 w-5 theme-text-muted" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-4 sm:p-6">
            {saveError && (
              <div
                id={saveErrorId}
                role="alert"
                aria-live="polite"
                className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4"
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-red-800">{saveError}</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={isSubmitting}
                    className="h-9 px-3 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                  >
                    Tentar novamente
                  </button>
                </div>
              </div>
            )}

            {presets.length > 0 && (
              <div className="mb-6 p-4 rounded-lg border theme-surface-muted" style={{ borderColor: 'var(--border)' }}>
                <h4 className="text-sm font-medium mb-3 flex items-center theme-text">
                  <Users className="h-4 w-4 mr-2" />
                  Aplicar Preset de Permissões
                </h4>
                <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                  <label htmlFor={presetSelectId} className="sr-only">
                    Preset de permissões
                  </label>
                  <select
                    ref={presetSelectRef}
                    id={presetSelectId}
                    value={selectedPreset}
                    onChange={(e) => handlePresetChange(e.target.value)}
                    disabled={isSubmitting}
                    className="h-11 flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-0 theme-surface disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ borderColor: 'var(--border)' }}
                  >
                    <option value="">Selecione um preset...</option>
                    {presets.map((preset) => (
                      <option key={preset.name} value={preset.name}>
                        {preset.label} - {preset.description}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleApplyPreset}
                    disabled={!selectedPreset || isSubmitting}
                    className="h-11 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  >
                    Aplicar
                  </button>
                </div>
              </div>
            )}

            {totalPermissions > 0 && (
              <div className="mb-6 rounded-lg border p-4 theme-surface-muted" style={{ borderColor: 'var(--border)' }}>
                <label htmlFor="permissions-search" className="block text-sm font-medium theme-text mb-2">
                  Pesquisar permissão ou categoria
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 theme-text-muted" />
                  <input
                    id="permissions-search"
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Ex.: financeiro, editar, dashboard..."
                    disabled={isSubmitting}
                    className="h-11 w-full pl-10 pr-4 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent theme-surface disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ borderColor: 'var(--border)' }}
                  />
                </div>
              </div>
            )}

            {totalPermissions > 0 && (
              <div
                className="mb-6 rounded-xl border p-4 sm:p-5 theme-surface"
                style={{
                  borderColor: 'var(--border)',
                  background: 'linear-gradient(180deg, var(--surface-muted) 0%, var(--surface) 100%)'
                }}
              >
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold theme-text">Ações em massa</p>
                      <p className="text-xs theme-text-muted">
                        Gerencie todas as permissões com um clique.
                      </p>
                    </div>
                    <div
                      className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium"
                      style={{ background: 'var(--surface-muted)', color: 'var(--text-primary)' }}
                    >
                      {selectedCount}/{totalPermissions} selecionadas
                    </div>
                  </div>

                  <div>
                    <div className="h-2 w-full rounded-full" style={{ background: 'var(--border)' }}>
                      <div
                        className="h-2 rounded-full transition-all duration-300"
                        style={{ width: `${completionRate}%`, background: 'var(--accent)' }}
                      />
                    </div>
                    <p className="mt-1 text-xs theme-text-muted">
                      {remainingPermissions === 0
                        ? 'Todas as permissões já estão selecionadas.'
                        : `${remainingPermissions} permissões ainda não selecionadas.`}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={handleSelectAllPermissions}
                      disabled={isSubmitting || selectedCount === totalPermissions}
                      className="h-11 w-full px-4 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 inline-flex items-center justify-center gap-2"
                    >
                      <CheckCheck className="h-4 w-4" />
                      <span className="sm:hidden">Marcar tudo</span>
                      <span className="hidden sm:inline">Marcar todas</span>
                    </button>
                    <button
                      type="button"
                      onClick={handleClearAllPermissions}
                      disabled={isSubmitting || selectedCount === 0}
                      className="h-11 w-full px-4 rounded-lg border text-sm font-medium theme-text hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 inline-flex items-center justify-center gap-2"
                      style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}
                    >
                      <RotateCcw className="h-4 w-4" />
                      <span className="sm:hidden">Limpar tudo</span>
                      <span className="hidden sm:inline">Desmarcar todas</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-6">
              {Object.keys(groupedPermissions).length === 0 ? (
                <div className="text-center py-8 theme-text-muted">
                  <Shield className="h-12 w-12 mx-auto mb-4 theme-text-muted" />
                  <p>Nenhuma permissão disponível para este usuário.</p>
                </div>
              ) : Object.keys(filteredGroupedPermissions).length === 0 ? (
                <div className="text-center py-8 theme-text-muted">
                  <Search className="h-10 w-10 mx-auto mb-3 theme-text-muted" />
                  <p>Nenhum resultado encontrado para "{searchTerm}".</p>
                </div>
              ) : (
                Object.entries(filteredGroupedPermissions).map(([category, categoryPermissions]) => (
                  <div key={category} className="border rounded-lg theme-surface" style={{ borderColor: 'var(--border)' }}>
                    <div className="px-4 py-3 theme-surface-muted border-b" style={{ borderColor: 'var(--border)' }}>
                      {(() => {
                        const selectedInCategory = categoryPermissions.filter((permission) =>
                          selectedPermissions.includes(permission.name)
                        ).length;
                        const totalInCategory = categoryPermissions.length;
                        const allSelectedInCategory = selectedInCategory === totalInCategory && totalInCategory > 0;
                        const noneSelectedInCategory = selectedInCategory === 0;

                        return (
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <h4 className="text-sm font-medium theme-text">{CATEGORY_LABELS[category] || category}</h4>
                          <span
                            className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                            style={{ background: 'var(--surface)', color: 'var(--text-muted)' }}
                          >
                            {selectedInCategory}/{totalInCategory}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 sm:flex items-center gap-2 w-full sm:w-auto">
                          <button
                            type="button"
                            onClick={() => handleSelectCategoryPermissions(categoryPermissions)}
                            disabled={isSubmitting || allSelectedInCategory}
                            className="h-8 w-full sm:w-auto px-2.5 rounded-md text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                          >
                            <span className="sm:hidden">Marcar</span>
                            <span className="hidden sm:inline">Marcar categoria</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleClearCategoryPermissions(categoryPermissions)}
                            disabled={isSubmitting || noneSelectedInCategory}
                            className="h-8 w-full sm:w-auto px-2.5 rounded-md text-xs font-medium border theme-text hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                            style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}
                          >
                            <span className="sm:hidden">Limpar</span>
                            <span className="hidden sm:inline">Desmarcar categoria</span>
                          </button>
                        </div>
                      </div>
                        );
                      })()}
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        {categoryPermissions.map((permission) => {
                          const isSelected = selectedPermissions.includes(permission.name);
                          const userHasPermission =
                            permissions && Array.isArray(permissions)
                              ? permissions.some((item) => item.name === permission.name)
                              : false;

                          return (
                            <label
                              key={permission.name}
                              className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                isSelected
                                  ? 'bg-blue-500/10 border-blue-400/40'
                                  : 'theme-surface border-[color:var(--border)] hover:opacity-90'
                              } ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                disabled={isSubmitting}
                                onChange={() => handlePermissionToggle(permission.name)}
                                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded flex-shrink-0 disabled:cursor-not-allowed"
                              />
                              <div className="flex-1 min-w-0 overflow-hidden">
                                <div className="flex items-center space-x-2">
                                  <span className="text-sm font-medium theme-text truncate">{permission.display_name}</span>
                                  {userHasPermission && (
                                    <CheckCircle
                                      className="h-4 w-4 text-green-500 flex-shrink-0"
                                      aria-label="Permissão já atribuída"
                                    />
                                  )}
                                </div>
                                <p className="text-xs theme-text-muted mt-1 break-words">{permission.description}</p>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="mt-6 p-4 rounded-lg border theme-surface-muted" style={{ borderColor: 'var(--border)' }}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center space-x-2 min-w-0">
                  <Shield className="h-4 w-4 theme-text-muted flex-shrink-0" />
                  <span className="text-sm theme-text truncate">{selectedPermissions.length} permissões selecionadas</span>
                </div>
                <div className="text-sm theme-text-muted flex-shrink-0">de {totalPermissions} disponíveis</div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 sm:p-6 border-t theme-surface-muted" style={{ borderColor: 'var(--border)' }}>
          <button
            type="button"
            onClick={requestClose}
            disabled={isSubmitting}
            className="h-11 px-4 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors theme-text focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSubmitting}
            className="h-11 flex items-center justify-center gap-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            {isSubmitting ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>{isSubmitting ? 'Salvando permissões...' : 'Salvar Permissões'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserPermissions;
